import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ClipboardPlus, Plus, Trash2, Check, Clock, Pill, Activity,
  Copy, Printer, ChevronDown, ChevronRight, X, FileText, Wind, Droplet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API } from "@/config/constants";
import { useOfflineData } from "@/contexts/OfflineDataContext";

const ROUTES = ["IV", "IM", "IN", "IO", "PO", "SL", "SC", "Inhaled"];
const DOSE_UNITS = ["g", "mg", "mcg", "mcg/kg/min", "mg/min", "mcg/min", "mL", "mL/hr"];

const COMMON_INTERVENTIONS = [
  "12-Lead ECG", "IV Access", "IO Access", "BGL Check", "SpO2 Monitor",
  "BP Check", "Airway Management", "BVM Ventilation", "CPAP Applied",
  "Splint Applied", "Tourniquet Applied", "Defibrillation", "Cardioversion",
  "Transcutaneous Pacing", "Suction", "NPA Inserted", "OPA Inserted",
];

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
}

function formatTime(t) {
  if (!t || t.length < 4) return t;
  return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
}

function EntryForm({ onAdd, suggestedDrugs = [], allDrugs = [] }) {
  const [name, setName] = useState("");
  const [selectedType, setSelectedType] = useState(null); // auto-detected
  const [doseValue, setDoseValue] = useState("");
  const [doseUnit, setDoseUnit] = useState("mg");
  const [route, setRoute] = useState("IV");
  const [time, setTime] = useState(nowTime());
  const [notes, setNotes] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const nameRef = useRef(null);
  const suggestRef = useRef(null);

  // Build searchable drug list with aliases
  const drugIndex = useMemo(() => {
    const items = [];
    const seen = new Set();
    for (const d of allDrugs) {
      if (!seen.has(d)) { seen.add(d); items.push(d); }
      const brandMatch = d.match(/\(([^)]+)\)/);
      if (brandMatch) {
        for (const alias of brandMatch[1].split(/,\s*/)) {
          const a = alias.trim();
          if (a.length > 1 && !seen.has(a)) { seen.add(a); items.push(a); }
        }
      }
    }
    return items;
  }, [allDrugs]);

  const drugSet = useMemo(() => new Set(allDrugs.map(d => d.toLowerCase())), [allDrugs]);

  // Unified filtered list — drugs + interventions together
  const filtered = useMemo(() => {
    const q = name.trim().toLowerCase();
    const results = [];

    // Drugs
    const drugMatches = new Set();
    if (!q) {
      allDrugs.slice(0, 6).forEach(d => drugMatches.add(d));
    } else {
      for (const d of allDrugs) {
        if (d.toLowerCase().includes(q)) drugMatches.add(d);
      }
      for (const alias of drugIndex) {
        if (alias.toLowerCase().includes(q)) {
          const parent = allDrugs.find(d => d.includes(alias) || d.toLowerCase().includes(alias.toLowerCase()));
          if (parent) drugMatches.add(parent);
        }
      }
    }
    for (const d of drugMatches) {
      results.push({ name: d, type: "drug" });
    }

    // Interventions
    const interventions = !q
      ? COMMON_INTERVENTIONS.slice(0, 4)
      : COMMON_INTERVENTIONS.filter(i => i.toLowerCase().includes(q));
    for (const i of interventions) {
      results.push({ name: i, type: "intervention" });
    }

    return results.slice(0, 12);
  }, [name, allDrugs, drugIndex]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const type = selectedType || (drugSet.has(name.trim().toLowerCase()) ? "drug" : "intervention");
    const dose = doseValue ? `${doseValue} ${doseUnit}` : "";
    onAdd({ type, name: name.trim(), dose, route, time, notes });
    setName("");
    setSelectedType(null);
    setDoseValue("");
    setDoseUnit("mg");
    setRoute("IV");
    setTime(nowTime());
    setNotes("");
    setShowSuggestions(false);
    nameRef.current?.focus();
  };

  const selectSuggestion = (item) => {
    setName(item.name);
    setSelectedType(item.type);
    setShowSuggestions(false);
  };

  const isDrug = selectedType === "drug" || (!selectedType && name && drugSet.has(name.trim().toLowerCase()));

  return (
    <Card className="border-primary/30" data-testid="entry-form">
      <CardContent className="pt-4 pb-3">
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="flex gap-2">
            <div className="flex-1 relative" ref={suggestRef}>
              <Input
                ref={nameRef}
                placeholder="Search drug or intervention..."
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setSelectedType(null);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="flex-1"
                data-testid="entry-name"
                autoComplete="off"
              />
              {showSuggestions && filtered.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-56 overflow-auto">
                  {filtered.map((item) => (
                    <button
                      key={`${item.type}-${item.name}`}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                      onMouseDown={() => selectSuggestion(item)}
                      data-testid={`search-suggest-${item.name.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {item.type === "drug" ? (
                        <Pill className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      ) : (
                        <Activity className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                      )}
                      <span className="flex-1">{item.name}</span>
                      <span className="text-xs text-muted-foreground">{item.type === "drug" ? "Drug" : "Intervention"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Input
              placeholder="Time"
              value={time}
              onChange={(e) => setTime(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="w-20 font-mono text-center"
              data-testid="entry-time"
            />
          </div>

          {isDrug && (
            <div className="flex gap-2 items-center">
              <Input
                placeholder="Dose"
                value={doseValue}
                onChange={(e) => setDoseValue(e.target.value)}
                className="w-24"
                data-testid="entry-dose"
              />
              <select
                value={doseUnit}
                onChange={(e) => setDoseUnit(e.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="entry-dose-unit"
              >
                {DOSE_UNITS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
              <select
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="entry-route"
              >
                {ROUTES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              placeholder="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="flex-1"
              data-testid="entry-notes"
            />
            <Button type="submit" size="sm" data-testid="add-entry-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function EntryRow({ entry, onDelete }) {
  const iconMap = {
    drug: <Pill className="w-4 h-4 text-amber-400 shrink-0" />,
    infusion: <Droplet className="w-4 h-4 text-purple-400 shrink-0" />,
    ventilation: <Wind className="w-4 h-4 text-cyan-400 shrink-0" />,
    intervention: <Activity className="w-4 h-4 text-blue-400 shrink-0" />,
  };
  const icon = iconMap[entry.type] || iconMap.intervention;
  return (
    <div
      className="flex items-center gap-3 py-2.5 px-3 hover:bg-accent/30 rounded-md group transition-colors"
      data-testid={`entry-row-${entry.id}`}
    >
      <span className="font-mono text-sm w-12 shrink-0">
        {formatTime(entry.time)}
      </span>
      {icon}
      <div className="flex-1 min-w-0">
        <span className="font-medium text-sm">{entry.name}</span>
        {entry.dose && (
          <span className="text-sm ml-2">{entry.dose}</span>
        )}
        {entry.route && (
          <Badge variant="outline" className="ml-2 text-xs">{entry.route}</Badge>
        )}
        {entry.notes && (
          <span className="text-xs ml-2 italic">— {entry.notes}</span>
        )}
      </div>
      <button
        onClick={() => onDelete(entry.id)}
        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
        data-testid={`delete-entry-${entry.id}`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function LogSummary({ log, onCopy }) {
  const lines = [];
  lines.push(`Trip: ${log.patient_label || "Unspecified"}`);
  lines.push(`Date: ${new Date(log.created_at).toLocaleDateString()}`);
  lines.push(`Status: ${log.status}`);
  lines.push("---");
  for (const e of log.entries || []) {
    const parts = [formatTime(e.time), e.name];
    if (e.dose) parts.push(e.dose);
    if (e.route) parts.push(e.route);
    if (e.notes) parts.push(`(${e.notes})`);
    lines.push(parts.join(" | "));
  }
  return lines.join("\n");
}

export default function InterventionTracker({ embedded = false }) {
  const [logs, setLogs] = useState([]);
  const [activeLog, setActiveLog] = useState(null);
  const [tripNumber, setTripNumber] = useState("");
  const [expandedPast, setExpandedPast] = useState(null);
  const [loading, setLoading] = useState(true);
  const { drugs: cachedDrugs } = useOfflineData();

  const allDrugNames = useMemo(() => 
    cachedDrugs.map((d) => d.name).sort(),
    [cachedDrugs]
  );

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/intervention-logs`);
      const all = res.data;
      setLogs(all);
      const active = all.find((l) => l.status === "active");
      if (active) setActiveLog(active);
    } catch (e) {
      console.error("Error fetching logs:", e);
    } finally {
      setLoading(false);
    }
  };

  const startNewLog = async () => {
    try {
      const res = await axios.post(`${API}/intervention-logs`, {
        patient_label: tripNumber || `Trip ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
      });
      setActiveLog(res.data);
      setLogs((prev) => [res.data, ...prev]);
      setTripNumber("");
      toast.success("Intervention log started");
    } catch (e) {
      toast.error("Failed to start log");
    }
  };

  const addEntry = async (entry) => {
    if (!activeLog) return;
    try {
      const res = await axios.post(`${API}/intervention-logs/${activeLog.id}/entries`, entry);
      setActiveLog((prev) => ({
        ...prev,
        entries: [...(prev.entries || []), res.data],
      }));
      setLogs((prev) =>
        prev.map((l) =>
          l.id === activeLog.id
            ? { ...l, entries: [...(l.entries || []), res.data] }
            : l
        )
      );
      toast.success(`${entry.name} logged at ${formatTime(entry.time)}`);
    } catch (e) {
      toast.error("Failed to add entry");
    }
  };

  const deleteEntry = async (entryId) => {
    if (!activeLog) return;
    try {
      await axios.delete(`${API}/intervention-logs/${activeLog.id}/entries/${entryId}`);
      setActiveLog((prev) => ({
        ...prev,
        entries: prev.entries.filter((e) => e.id !== entryId),
      }));
      setLogs((prev) =>
        prev.map((l) =>
          l.id === activeLog.id
            ? { ...l, entries: l.entries.filter((e) => e.id !== entryId) }
            : l
        )
      );
    } catch (e) {
      toast.error("Failed to delete entry");
    }
  };

  const completeLog = async () => {
    if (!activeLog) return;
    try {
      await axios.patch(`${API}/intervention-logs/${activeLog.id}`, { status: "completed" });
      setActiveLog(null);
      fetchLogs();
      toast.success("Intervention log completed");
    } catch (e) {
      toast.error("Failed to complete log");
    }
  };

  const deleteLog = async (logId) => {
    try {
      await axios.delete(`${API}/intervention-logs/${logId}`);
      if (activeLog?.id === logId) setActiveLog(null);
      setLogs((prev) => prev.filter((l) => l.id !== logId));
      toast.success("Log deleted");
    } catch (e) {
      toast.error("Failed to delete log");
    }
  };

  const copyToClipboard = (log) => {
    const text = LogSummary({ log });
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard");
    }).catch(() => toast.error("Copy failed"));
  };

  const handlePrint = (log) => {
    const text = LogSummary({ log });
    const win = window.open("", "_blank");
    win.document.write(`<pre style="font-family:monospace;font-size:14px;padding:20px;">${text}</pre>`);
    win.document.close();
    win.print();
  };

  const pastLogs = logs.filter((l) => l.status === "completed");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-4" : "max-w-3xl mx-auto space-y-6"} data-testid="intervention-tracker">
      {/* Header — hidden when embedded inside the Sheet (which provides its own) */}
      {!embedded && (
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold flex items-center gap-3" data-testid="intervention-tracker-title">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
              <ClipboardPlus className="w-5 h-5 text-emerald-500" />
            </div>
            Intervention Tracker
          </h1>
          <p className="text-muted-foreground mt-1 ml-[52px]">
            Log drugs and interventions with timestamps — review log for your PCR
          </p>
        </div>
      )}

      {/* Active Log or Start New */}
      {!activeLog ? (
        <Card className="border-emerald-500/30 bg-emerald-500/5" data-testid="start-log-card">
          <CardContent className="pt-5 pb-4">
            <p className="text-sm text-muted-foreground mb-3">Start a new intervention log for this trip:</p>
            <div className="flex gap-2">
              <Input
                placeholder="Trip Number"
                value={tripNumber}
                onChange={(e) => setTripNumber(e.target.value)}
                className="flex-1"
                data-testid="patient-label-input"
                onKeyDown={(e) => e.key === "Enter" && startNewLog()}
              />
              <Button onClick={startNewLog} data-testid="start-log-btn">
                <Plus className="w-4 h-4 mr-1" /> Start Log
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Active log header */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                <Clock className="w-3 h-3 mr-1" /> Active
              </Badge>
              <span className="font-medium text-sm">{activeLog.patient_label || "Current Trip"}</span>
              <span className="text-xs text-muted-foreground">
                {new Date(activeLog.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            <div className="flex gap-1 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(activeLog)}
                data-testid="copy-log-btn"
              >
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePrint(activeLog)}
                data-testid="print-log-btn"
              >
                <Printer className="w-3.5 h-3.5 mr-1" /> Print
              </Button>
              <Button
                variant="default"
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={completeLog}
                data-testid="complete-log-btn"
              >
                <Check className="w-3.5 h-3.5 mr-1" /> End Call
              </Button>
            </div>
          </div>

          {/* Entry form */}
          <EntryForm onAdd={addEntry} allDrugs={allDrugNames} />

          {/* Entries list */}
          {(activeLog.entries?.length || 0) > 0 ? (
            <Card data-testid="entries-list">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Log ({activeLog.entries.length} entries)
                </CardTitle>
              </CardHeader>
              <CardContent className="px-2 pb-2">
                <div className="divide-y divide-border">
                  {activeLog.entries.map((e) => (
                    <EntryRow key={e.id} entry={e} onDelete={deleteEntry} />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <p className="text-center text-muted-foreground py-8 text-sm">
              No entries yet — add your first intervention above
            </p>
          )}
        </div>
      )}

      {/* Past Logs */}
      {pastLogs.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading text-xl font-semibold">Past Logs</h2>
          {pastLogs.map((log) => (
            <Card
              key={log.id}
              className="cursor-pointer hover:border-border/80 transition-colors"
              data-testid={`past-log-${log.id}`}
            >
              <CardContent className="py-3 px-4">
                <div
                  className="flex items-center justify-between"
                  onClick={() => setExpandedPast(expandedPast === log.id ? null : log.id)}
                >
                  <div className="flex items-center gap-2">
                    {expandedPast === log.id ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="font-medium text-sm">{log.patient_label || "Trip"}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.created_at).toLocaleDateString()} {new Date(log.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <Badge variant="outline" className="text-xs">{log.entries?.length || 0} entries</Badge>
                  </div>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(log)}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handlePrint(log)}>
                      <Printer className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-destructive" onClick={() => deleteLog(log.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {expandedPast === log.id && (
                  <div className="mt-3 pt-3 border-t border-border divide-y divide-border">
                    {(log.entries || []).map((e) => (
                      <EntryRow key={e.id} entry={e} onDelete={() => {}} />
                    ))}
                    {(!log.entries || log.entries.length === 0) && (
                      <p className="text-sm text-muted-foreground py-2">No entries recorded</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
